import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import OptOutFormFr from '../components/OptOutFormFr';
import { PhoneOff, Info, Mail, Clock, CheckCircle2 } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';
import HeroSection from '../components/HeroSection';
import FAQSection from '../components/FAQSection';
import ContactSection from '../components/ContactSection';
import Footer from '../components/Footer';
import OptOutSection from '../components/OptOutSection';

const IndexFr: React.FC = () => {
  const isMobile = useIsMobile();
  const [urlParams, setUrlParams] = useState<Record<string, string>>({});
  const [nanoid, setNanoid] = useState<string | null>(null);
  
  useEffect(() => {
    // Get URL parameters when component mounts
    const params = new URLSearchParams(window.location.search);
    const paramsObject: Record<string, string> = {};
    
    // Convert URLSearchParams to a regular object
    params.forEach((value, key) => {
      paramsObject[key] = value;
    });
    
    setUrlParams(paramsObject);
    
    // Check if 't' parameter exists and set nanoid
    const tParam = params.get('t');
    if (tParam) {
      setNanoid(tParam);
    }
  }, []);
  
  const faqs = [
    {
      question: "Combien de temps faut-il pour traiter ma demande de désabonnement ?",
      answer: "Votre demande de désabonnement sera traitée dans un délai de 10 jours ouvrables. Vous pourriez continuer à recevoir des messages qui ont été programmés avant le traitement de votre demande."
    },
    {
      question: "Vais-je arrêter de recevoir tous les messages immédiatement ?",
      answer: "Vous pourriez continuer à recevoir des messages qui ont été programmés avant votre demande de désabonnement. De plus, les messages transactionnels et de service peuvent toujours être envoyés s'ils sont liés à votre compte ou service."
    },
    {
      question: "Puis-je me réabonner ultérieurement ?",
      answer: "Oui, vous pouvez vous réabonner pour recevoir des SMS en contactant notre équipe de service client."
    },
    {
      question: "Cela affectera-t-il d'autres canaux de communication ?",
      answer: "Non, le désabonnement des SMS n'affectera pas les autres canaux de communication comme l'e-mail ou le courrier postal."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-white to-optout-gray">
      <Header isFrench={true} />
      
      <main className="flex-1 flex flex-col">
        {/* Hero Section */}
        <section className="relative px-4 py-16 md:py-24">
          <div className="container mx-auto max-w-5xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1 animate-fade-in">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="bg-optout-lightblue h-6 rounded-full px-3 py-1">
                    <span className="text-xs font-medium text-optout-blue">Processus Simple</span>
                  </div>
                </div>
                
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-optout-text mb-4 leading-tight text-balance">
                  Arrêtez de Recevoir les SMS Marketing
                </h1>
                
                <p className="text-optout-lighttext mb-8 text-balance">
                  Complétez notre processus simple de désabonnement pour arrêter de recevoir des messages marketing par SMS.
                  Votre vie privée est importante pour nous, et nous vous facilitons la gestion de vos préférences de communication.
                </p>
                
                <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                  <a 
                    href="#opt-out-form" 
                    className="btn-primary px-6 py-3 rounded-lg text-center inline-block"
                  >
                    Se Désabonner Maintenant
                  </a>
                  
                  <a 
                    href="#faq" 
                    className="px-6 py-3 rounded-lg text-center inline-block border border-optout-blue text-optout-blue hover:bg-optout-lightblue transition-all duration-300"
                  >
                    En Savoir Plus
                  </a>
                </div>
              </div>
              
              <div className="order-1 md:order-2 flex justify-center md:justify-end animate-fade-in">
                <div className="relative hidden md:block">
                  <div className="absolute inset-0 bg-optout-blue rounded-3xl opacity-5 blur-xl transform -rotate-6"></div>
                  <div className="glass-card rounded-3xl overflow-hidden p-4 relative z-10">
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <PhoneOff className="h-5 w-5 text-optout-blue" />
                          <span className="font-medium text-optout-text">Désabonnement SMS</span>
                        </div>
                        <div className="bg-green-100 rounded-full px-2 py-1">
                          <span className="text-xs font-medium text-green-600">Simple</span>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <div className="bg-optout-gray p-2 rounded-full">
                            <CheckCircle2 className="h-4 w-4 text-optout-blue" />
                          </div>
                          <span className="text-sm text-optout-text">Entrez votre numéro de téléphone</span>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <div className="bg-optout-gray p-2 rounded-full">
                            <CheckCircle2 className="h-4 w-4 text-optout-blue" />
                          </div>
                          <span className="text-sm text-optout-text">Soumettez votre demande</span>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <div className="bg-optout-gray p-2 rounded-full">
                            <CheckCircle2 className="h-4 w-4 text-optout-blue" />
                          </div>
                          <span className="text-sm text-optout-text">Recevez confirmation</span>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <div className="bg-optout-gray p-2 rounded-full">
                            <Clock className="h-4 w-4 text-optout-lighttext" />
                          </div>
                          <span className="text-sm text-optout-lighttext">Traitement sous 10 jours</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Opt-Out Form Section */}
        <section id="opt-out-form" className="bg-optout-gray py-16 md:py-24">
          <div className="container mx-auto max-w-5xl px-4">
            <OptOutFormFr nanoidValue={nanoid} />
          </div>
        </section>
        
        {/* FAQ Section */}
        <FAQSection faqs={faqs} />
        
        {/* Contact Section */}
        <ContactSection />
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default IndexFr;
